import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ArrowRightLeft, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { CompanyCombobox } from "@/components/ui/CompanyCombobox.js";
import { api } from "@/lib/api.js";
import { useAuthStore } from "@/lib/auth.js";
import { relativeTime } from "@/lib/format.js";

interface Agent {
  idusuario: number;
  email: string | null;
  fullName: string | null;
  rol: number | null;
  rolLabel: string | null;
  estado: number | null;
  estadoLabel: string | null;
  idcompany: number | null;
  companyName: string | null;
  createdAt: string | null;
  phone: string | null;
  position: string | null;
}

interface AgentDetail extends Agent {
  disabledAt: string | null;
  extension: number | null;
  idskill: number | null;
  activeSessions: number;
}

function MoveModal({
  agent,
  onClose,
  onMoved,
}: {
  agent: Agent;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [target, setTarget] = useState<number | null>(null);
  const [targetName, setTargetName] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const move = useMutation({
    mutationFn: () =>
      api.patch(`/agent-users/${agent.idusuario}/company`, {
        targetIdcompany: Number(target),
        reason,
      }),
    onSuccess: () => {
      toast.success("Agente movido correctamente");
      onMoved();
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 grid place-items-center p-4">
      <div className="card w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-lg">Mover agente a otra company</h2>
            <p className="text-xs text-slate-500 mt-1">
              Esta acción cambia <span className="mono">IDCOMPANY</span> en el core. Queda auditada.
            </p>
          </div>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 text-sm">
          <p className="font-medium">{agent.fullName ?? "—"}</p>
          <p className="text-xs mono text-slate-500">{agent.email}</p>
          <p className="text-xs text-slate-500 mt-1">
            Actual: <span className="font-medium">{agent.companyName ?? "—"}</span> (
            <span className="mono">{agent.idcompany}</span>)
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Mover a company *</label>
          <CompanyCombobox
            value={target}
            valueName={targetName}
            excludeId={agent.idcompany}
            placeholder="Busca por nombre o ID de company…"
            onChange={(id, name) => {
              setTarget(id);
              setTargetName(name);
            }}
          />
          <p className="text-xs text-slate-400">
            Puedes escribir el nombre o el número de IDCOMPANY.
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Motivo del cambio *</label>
          <textarea
            className="input min-h-[80px]"
            placeholder="Describe brevemente por qué se mueve este agente…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            minLength={3}
            maxLength={500}
          />
        </div>

        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary flex-1"
            disabled={!target || reason.trim().length < 3 || move.isPending}
            onClick={() => move.mutate()}
          >
            {move.isPending ? "Moviendo…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AgentsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState<string>("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [moving, setMoving] = useState<Agent | null>(null);
  const pageSize = 25;
  const canMove = useAuthStore((s) => s.hasPermission("agent.move_company"));

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (q) p.set("q", q);
    if (estado) p.set("estado", estado);
    if (companyFilter) p.set("idcompany", companyFilter);
    return p.toString();
  }, [q, estado, companyFilter, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["agents", params],
    queryFn: () =>
      api.get<{ items: Agent[]; total: number }>(`/agent-users?${params}`),
  });

  const columns: Column<Agent>[] = [
    {
      header: "ID",
      width: "90px",
      render: (r) => <span className="mono text-xs">{r.idusuario}</span>,
    },
    {
      header: "Agente",
      render: (r) => (
        <div>
          <div className="font-medium">{r.fullName ?? "—"}</div>
          <div className="text-xs mono text-slate-500">{r.email ?? "—"}</div>
        </div>
      ),
    },
    {
      header: "Company",
      render: (r) => (
        <div>
          <div>{r.companyName ?? "—"}</div>
          <div className="text-xs mono text-slate-500">{r.idcompany}</div>
        </div>
      ),
    },
    { header: "Rol", render: (r) => r.rolLabel ?? "—", width: "110px" },
    {
      header: "Estado",
      render: (r) => <StatusBadge value={r.estadoLabel} />,
      width: "120px",
    },
    {
      header: "Creado",
      render: (r) => <span className="text-xs">{relativeTime(r.createdAt)}</span>,
      width: "120px",
    },
    {
      header: "",
      width: "120px",
      render: (r) =>
        canMove ? (
          <button
            className="btn-secondary"
            onClick={() => setMoving(r)}
            title="Mover a otra company"
          >
            <ArrowRightLeft size={12} className="mr-1" />
            Mover
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agentes"
        description="Usuarios del core de Gina. Puedes ver, filtrar y reasignar a otra company."
      />

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className="input pl-9"
            placeholder="Buscar por email, nombre o ID…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="input max-w-[180px]"
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="1">Activos</option>
          <option value="0">Inactivos</option>
          <option value="2">Suspendidos</option>
          <option value="3">Bloqueados</option>
        </select>
        <input
          className="input max-w-[140px]"
          placeholder="ID company…"
          value={companyFilter}
          onChange={(e) => {
            setCompanyFilter(e.target.value);
            setPage(1);
          }}
        />
        <span className="ml-auto text-xs text-slate-500 self-center">
          {data?.total ?? 0} agentes
        </span>
      </div>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        getKey={(r) => r.idusuario}
        empty="Sin agentes que coincidan con los filtros."
      />

      {data && data.total > pageSize && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, data.total)} de {data.total}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <button
              className="btn-secondary"
              disabled={page * pageSize >= data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {moving && (
        <MoveModal
          agent={moving}
          onClose={() => setMoving(null)}
          onMoved={() => qc.invalidateQueries({ queryKey: ["agents"] })}
        />
      )}
    </div>
  );
}
